import { useState } from 'react';
import { approvalScopeKey, useApprovalScope } from './useApprovalScope';
import {
    authorisationFigures,
    confirmRequiredReason,
} from '../utils/fundingAuthorisation';
import {
    CHECK_CODE_REASON,
    ENTER_CODE_REASON,
    REQUEST_APPROVAL_REASON,
    SELECTION_CHANGED_REASON,
} from '../utils/fundingApprovalCopy';
import { isCompleteCode } from '../utils/fundingCode';
import { useFundingCodeCheck } from './useFundingCodeCheck';
import { useFundingDelegation } from './useFundingDelegation';
import { useBalanceRejectionRefresh } from './useBalanceRejectionRefresh';
import type { FundingCodeCheck } from './useFundingCodeCheck';
import type { FundingApproverForm } from './useFundingApproverForm';
import type { AuthorisationFigures } from '../utils/fundingAuthorisation';
import type {
    FundingOverridePayload,
    FundingPurpose,
    InvoiceLinePayload,
    PendingFundingApproval,
    RejectionRecorder,
} from '../types/agreements.types';

interface Options {
    agreementId: string | undefined;
    purpose: FundingPurpose;
    lines: InvoiceLinePayload[];
    excessGross: number;
    selectionGross: number;
    cashPosition: number | undefined;
    canAuthorise: boolean;
    beginRejection?: () => RejectionRecorder;
}

export interface FundingApproval {
    mode: 'self' | 'delegated';
    confirmed: boolean;
    setConfirmed: (next: boolean) => void;
    figures: AuthorisationFigures;
    blockingReason: string | null;
    override: FundingOverridePayload | undefined;
    approverForm: FundingApproverForm;
    pending: PendingFundingApproval | null;
    code: string;
    setCode: (raw: string) => void;
    requestError: string | null;
    notice: string | null;
    emailOpen: boolean;
    reopenEmail: () => void;
    resend: () => Promise<void>;
    sending: boolean;
    resentAt: Date | null;
    codeCheck: FundingCodeCheck;
    resetCodeCheck: () => void;
}

interface Confirmation {
    token: number;
    excess: number | null;
}

/** The confirmation is bound to the agreement, purpose and excess it was given for and is
 *  discarded during render when any changes. Component state only, never persisted or logged. */
export function useFundingApproval({
    agreementId,
    purpose,
    lines,
    excessGross,
    selectionGross,
    cashPosition,
    canAuthorise,
    beginRejection,
}: Options): FundingApproval {
    const scope = useApprovalScope(approvalScopeKey(agreementId, purpose));
    const [confirmation, setConfirmation] = useState<Confirmation>({ token: scope.token, excess: null });
    const refreshBalance = useBalanceRejectionRefresh();
    const delegation = useFundingDelegation({
        agreementId,
        purpose,
        lines,
        beginRejection: () => {
            const record = beginRejection?.();
            return (requestedFor, reasons) => {
                record?.(requestedFor, reasons);
                void refreshBalance(requestedFor, reasons);
            };
        },
    });
    const codeCheck = useFundingCodeCheck({
        agreementId,
        purpose,
        approvalId: delegation.pending?.approvalId ?? null,
        code: delegation.code,
        lines,
        beginRejection,
    });

    const superseded =
        confirmation.token !== scope.token ||
        (confirmation.excess !== null && confirmation.excess !== excessGross);
    let current = confirmation;
    if (superseded) {
        current = { token: scope.token, excess: null };
        setConfirmation(current);
    }

    const mode = canAuthorise ? 'self' : 'delegated';
    const confirmed = excessGross > 0 && current.excess === excessGross;
    const codeReady = delegation.pending !== null && isCompleteCode(delegation.code);
    const accepted = codeReady && codeCheck.status === 'accepted';

    const blockingReason = (() => {
        if (excessGross === 0) return null;
        if (mode === 'self') return confirmed ? null : confirmRequiredReason(purpose);
        if (delegation.selectionChanged) return SELECTION_CHANGED_REASON;
        if (!delegation.pending) return REQUEST_APPROVAL_REASON;
        if (!codeReady) return ENTER_CODE_REASON;
        return accepted ? null : CHECK_CODE_REASON;
    })();

    const override = (() => {
        if (mode === 'self')
            return confirmed ? { purpose, self_approved: true as const } : undefined;
        if (!delegation.pending || !accepted || excessGross === 0) return undefined;
        return { purpose, approval_id: delegation.pending.approvalId, code: delegation.code };
    })();

    return {
        mode,
        confirmed,
        setConfirmed: (next) => setConfirmation({ token: scope.token, excess: next ? excessGross : null }),
        figures: authorisationFigures(selectionGross, cashPosition, excessGross),
        blockingReason,
        override,
        approverForm: delegation.approverForm,
        pending: delegation.pending,
        code: delegation.code,
        setCode: delegation.setCode,
        requestError: delegation.requestError,
        notice: delegation.notice,
        emailOpen: delegation.emailOpen,
        reopenEmail: () => {
            codeCheck.reset();
            delegation.reopenEmail();
        },
        resend: async () => {
            codeCheck.reset();
            await delegation.resend();
        },
        sending: delegation.sending,
        resentAt: delegation.resentAt,
        codeCheck,
        resetCodeCheck: codeCheck.reset,
    };
}
